import { useEffect, useRef } from 'react';
import { Check, Sparkles, ArrowRight } from 'lucide-react';
import { buttonVariants } from './ui/Button';
import { LocalizedAnchor } from './LocalizedLink';
import { PlanWizardButton } from './PlanWizard';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';

// Pricing strings (pricing.*.price, pricing.*.subtext, pricing.*.f1-f5) live in
// i18n/en.json and i18n/nl.json. Keep them in sync with packages/types/src/plans.ts.

/* ---- Data ---- */

interface PricingTier {
  key: string;
  highlighted?: boolean;
  badge?: string;
  features: string[];
  ctaVariant: 'gradient' | 'outline';
  ctaLink: string;
}

const tiers: PricingTier[] = [
  {
    key: 'free',
    features: [
      'pricing.free.f1',
      'pricing.free.f2',
      'pricing.free.f3',
      'pricing.free.f4',
      'pricing.free.f5',
      'pricing.free.f6',
      'pricing.free.f7',
      'pricing.free.f8',
      'pricing.free.f9',
    ],
    ctaVariant: 'outline',
    ctaLink: 'https://app.logpulse.io/sign-up',
  },
  {
    key: 'pro',
    highlighted: true,
    badge: 'pricing.pro.badge',
    features: [
      'pricing.pro.f1',
      'pricing.pro.f2',
      'pricing.pro.f3',
      'pricing.pro.f4',
      'pricing.pro.f5',
      'pricing.pro.f6',
    ],
    ctaVariant: 'gradient',
    ctaLink: 'https://app.logpulse.io/sign-up?tier=pro',
  },
  {
    key: 'business',
    features: [
      'pricing.business.f1',
      'pricing.business.f2',
      'pricing.business.f3',
      'pricing.business.f4',
      'pricing.business.f5',
    ],
    ctaVariant: 'outline',
    ctaLink: 'https://app.logpulse.io/sign-up?tier=business',
  },
  {
    key: 'enterprise',
    features: [
      'pricing.enterprise.f1',
      'pricing.enterprise.f2',
      'pricing.enterprise.f3',
      'pricing.enterprise.f4',
      'pricing.enterprise.f5',
    ],
    ctaVariant: 'outline',
    ctaLink: '/contact',
  },
];

const betaPerks = [
  'pricing.beta.perk1',
  'pricing.beta.perk2',
  'pricing.beta.perk3',
  'pricing.beta.perk4',
] as const;

interface FaqItem {
  q: string;
  a: string;
}

const faqKeys: FaqItem[] = [
  { q: 'pricing.faq.q1', a: 'pricing.faq.a1' },
  { q: 'pricing.faq.q2', a: 'pricing.faq.a2' },
  { q: 'pricing.faq.q3', a: 'pricing.faq.a3' },
  { q: 'pricing.faq.q4', a: 'pricing.faq.a4' },
  { q: 'pricing.faq.q5', a: 'pricing.faq.a5' },
];

/* ---- Components ---- */

function PricingCard({ tier }: { tier: PricingTier }) {
  const t = useT();

  return (
    <article
      className={cn(
        'relative isolate flex flex-col rounded-2xl p-8 sm:p-10 transition-all duration-200 ease-out',
        'hover:-translate-y-1 hover:shadow-xl',
        'focus-within:-translate-y-1 focus-within:shadow-xl',
        tier.highlighted
          ? 'border-2 border-transparent lg:-my-4 lg:py-12 shadow-xl shadow-primary-200/40 dark:shadow-primary-900/30'
          : 'bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800/60 shadow-md shadow-gray-200/50 dark:shadow-none',
      )}
      aria-label={`Pricing tier: ${t(`pricing.${tier.key}.name`)}`}
    >
      {/* Highlighted tier: a purple gradient "border" sits behind an opaque
          card surface. The <article> is `isolate` so it is always its own
          stacking context. Otherwise the hover/focus `transform` would
          promote it to one at hover time, lifting the -z gradient on top of
          the surface and turning the whole card purple (hiding the text). */}
      {tier.highlighted && (
        <>
          <div
            className="absolute -inset-[2px] rounded-2xl -z-20 shadow-lg shadow-primary-300/30 dark:shadow-primary-900/20"
            style={{
              background: 'linear-gradient(135deg, #3B9C93, #22807A, #1B6660)',
            }}
          />
          <div className="absolute inset-0 -z-10 rounded-2xl bg-white dark:bg-gray-900/80" />
        </>
      )}

      {/* Badge */}
      {tier.badge && (
        <div className="absolute -top-3.5 right-6">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider text-white bg-gradient-to-r from-primary-600 to-primary-500 shadow-lg shadow-primary-500/25">
            <Sparkles className="h-3 w-3" />
            {t(tier.badge)}
          </span>
        </div>
      )}

      {/* Tier name */}
      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
        {t(`pricing.${tier.key}.name`)}
      </h3>

      {/* Target audience */}
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        {t(`pricing.${tier.key}.target`)}
      </p>

      {/* Price */}
      <div className="mt-6 mb-6 pb-6 border-b border-gray-200/80 dark:border-gray-800/60">
        <span className="text-3xl font-extrabold text-gradient">
          {t(`pricing.${tier.key}.price`)}
        </span>
        <p className="mt-1.5 text-sm text-gray-500 italic">
          {t(`pricing.${tier.key}.subtext`)}
        </p>
      </div>

      {/* Features */}
      <ul className="space-y-3.5 flex-1" role="list">
        {tier.features.map((fKey) => (
          <li key={fKey} className="flex items-start gap-3">
            <Check
              className="h-5 w-5 text-success-500 dark:text-success-400 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {t(fKey)}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-8">
        <a
          href={tier.ctaLink}
          className={cn(
            buttonVariants({ variant: tier.ctaVariant, size: 'lg' }),
            'w-full justify-center h-12',
          )}
        >
          {t(`pricing.${tier.key}.cta`)}
          <ArrowRight className="h-4 w-4 ml-1.5" />
        </a>
      </div>
    </article>
  );
}

function FaqAccordion() {
  const t = useT();

  return (
    <div className="space-y-4">
      {faqKeys.map((item, i) => (
        <details
          key={i}
          className="group rounded-xl border border-gray-200 dark:border-gray-800/60 bg-white dark:bg-gray-900/30 shadow-sm shadow-gray-100 dark:shadow-none overflow-hidden"
        >
          <summary className="flex items-center justify-between cursor-pointer px-6 py-4 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800/30 transition-colors list-none [&::-webkit-details-marker]:hidden">
            <span>{t(item.q)}</span>
            <span className="ml-4 shrink-0 text-gray-400 dark:text-gray-500 transition-transform duration-200 group-open:rotate-45 text-lg">
              +
            </span>
          </summary>
          <div className="px-6 pb-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {t(item.a)}
          </div>
        </details>
      ))}
    </div>
  );
}

/* ---- Section ---- */

export function PricingSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackEvent('pricing_viewed');
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="pricing" className="py-24 sm:py-32 bg-gray-50 dark:bg-transparent" aria-labelledby="pricing-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mx-auto text-center mb-16 sm:mb-20 animate-on-scroll">
          <h2
            id="pricing-heading"
            className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance"
          >
            {t('pricing.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('pricing.subtitle')}
          </p>
          <div className="mt-8 flex justify-center">
            <PlanWizardButton />
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-5 items-start mb-20 sm:mb-24 animate-on-scroll">
          {tiers.map((tier) => (
            <PricingCard key={tier.key} tier={tier} />
          ))}
        </div>

        {/* Compare all plans link */}
        <div className="text-center -mt-10 mb-20 sm:mb-24 animate-on-scroll">
          <LocalizedAnchor
            href="/pricing/compare"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
          >
            {t('pricing.compareCta')}
            <ArrowRight className="h-4 w-4" />
          </LocalizedAnchor>
        </div>

        {/* Launch offer callout */}
        <div className="animate-on-scroll max-w-3xl mx-auto mb-20 sm:mb-24">
          <div className="relative rounded-2xl border border-primary-200 dark:border-primary-500/20 bg-primary-50 dark:bg-primary-900/10 px-8 sm:px-12 py-10 sm:py-12 overflow-hidden shadow-md shadow-primary-100/60 dark:shadow-none">
            {/* Subtle glow */}
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-primary-500/10 blur-3xl pointer-events-none" />

            <div className="relative text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 mb-5">
                <Sparkles className="h-3.5 w-3.5 text-primary-500" />
                <span className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
                  {t('pricing.beta.badge')}
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                {t('pricing.beta.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto mb-8">
                {t('pricing.beta.description')}
              </p>

              {/* Launch offer perks */}
              <div className="grid sm:grid-cols-2 gap-3 max-w-lg mx-auto mb-8 text-left">
                {betaPerks.map((perk) => (
                  <div key={perk} className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-success-500 dark:text-success-400 shrink-0" aria-hidden="true" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t(perk)}</span>
                  </div>
                ))}
              </div>

              <a
                href="https://app.logpulse.io/sign-up"
                className={cn(
                  buttonVariants({ variant: 'gradient', size: '2xl' }),
                )}
              >
                {t('pricing.beta.cta')}
                <ArrowRight className="h-5 w-5 ml-1" />
              </a>

              <p className="mt-4 text-xs text-gray-500">
                {t('pricing.beta.launch')}
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="animate-on-scroll max-w-2xl mx-auto">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-8">
            {t('pricing.faq.title')}
          </h3>
          <FaqAccordion />
        </div>
      </div>
    </section>
  );
}
